import React, { useEffect, useState, useRef } from 'react';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import InquiryBanner from './InquiryBanner.jsx';
import banner from '../assets/banners/img2.avif';
import { FiSettings, FiCheckCircle, FiLayers, FiShield, FiTrendingUp, FiAward, FiArrowRight } from 'react-icons/fi';
import p3 from '../assets/products/globe_valves.jpg';
import globeMatrix from '../assets/globevalves/globe_matrix.png';
import globe1 from '../assets/globevalves/globe1.PNG';
import globe2 from '../assets/globevalves/globe_2.PNG';
import '../css/globeValves.css';

const valveTypes = [
  {
    name: 'Bolted Bonnet Globe Valves',
    description: 'Globe valves with bolted bonnet design ensuring secure sealing and easy access for maintenance operations.',
    features: ['Secure bonnet attachment', 'Easy maintenance access', 'High-pressure capability', 'Reliable sealing']
  },
  {
    name: 'Gear Operated Globe Valves',
    description: 'Globe valves with optional gear operation for easier handling in high-pressure applications.',
    features: ['Reduced operating torque', 'Easy operation', 'Suitable for large sizes', 'Manual or gear operation']
  },
  {
    name: 'Cryogenic Globe Valves',
    description: 'Specialized globe valves designed for extremely low-temperature applications in cryogenic systems.',
    features: ['Low-temperature operation', 'Extended bonnet design', 'Special material selection', 'Cryogenic sealing']
  },
  {
    name: 'High-Pressure Globe Valves',
    description: 'Heavy-duty globe valves designed for high-pressure applications requiring superior performance.',
    features: ['High-pressure ratings', 'Robust construction', 'Excellent throttling', 'Reliable operation']
  }
];

const specifications = [
  {
    title: 'Size Range',
    value: '2" – 16"',
    icon: <FiSettings />
  },
  {
    title: 'Pressure Rating',
    value: 'Class 150 – 2500',
    icon: <FiTrendingUp />
  },
  {
    title: 'Materials',
    value: 'Carbon Steel, Stainless Steel, Alloy',
    icon: <FiLayers />
  },
  {
    title: 'Standards',
    value: 'BS 1873, API 623',
    icon: <FiAward />
  }
];

const advantages = [
  {
    icon: <FiSettings />,
    title: 'Excellent Throttling',
    description: 'Globe valves provide superior flow control and throttling capabilities, making them ideal for applications requiring precise flow regulation and modulation.'
  },
  {
    icon: <FiShield />,
    title: 'Reliable Sealing',
    description: 'Metal-to-metal seating provides excellent leak-tight performance and reliable shut-off, suitable for high-pressure and critical service conditions.'
  },
  {
    icon: <FiAward />,
    title: 'Industry Standards',
    description: 'Manufactured to BS 1873 and API 623 standards, ensuring consistent quality and compliance with international specifications for globe valve applications.'
  },
  {
    icon: <FiTrendingUp />,
    title: 'Versatile Applications',
    description: 'Suitable for a wide range of applications from high-pressure steam systems to cryogenic services, offering flexibility in industrial operations.'
  }
];

const applications = [
  {
    title: 'Steam Systems',
    description: 'Power generation and process industries requiring reliable throttling and isolation in high-temperature steam applications.'
  },
  {
    title: 'Chemical Processing',
    description: 'Chemical plants handling corrosive and hazardous materials where precise flow control and bubble-tight sealing are essential.'
  },
  {
    title: 'Oil & Gas Industry',
    description: 'Refineries and petrochemical plants requiring precise flow control and reliable shut-off in hydrocarbon processing.'
  },
  {
    title: 'Water Treatment',
    description: 'Municipal water systems and wastewater treatment plants requiring durable valves for flow regulation and control.'
  },
  {
    title: 'HVAC Systems',
    description: 'Heating, ventilation, and air conditioning systems requiring precise flow control and temperature regulation.'
  },
  {
    title: 'Marine Applications',
    description: 'Marine and offshore platforms where corrosion resistance and reliable throttling are critical in harsh environments.'
  }
];

const features = [
  'Bolted Bonnet design',
  'Optional Gear operation',
  'Cryogenic applications',
  'Excellent throttling capability',
  'High-pressure ratings',
  'Metal-to-metal sealing',
  'Fire-safe design options',
  'Multiple material options'
];

const GlobeValves = () => {
  const sectionsRef = useRef([]);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('scroll-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    sectionsRef.current.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      sectionsRef.current.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Header />
      <div className="glv-banner">
        <img
          src={banner}
          alt="Globe Valves Banner"
          className="glv-banner__img"
        />
        <div className="glv-banner__overlay"></div>
        <div className="glv-banner__content">
          <h2 className="glv-banner__title">Globe Valves</h2>
          <div className="glv-banner__subtitle">
            Precision-engineered globe valves for fine flow regulation and control in critical applications
          </div>
        </div>
      </div>

      <div 
        ref={(el) => (sectionsRef.current[0] = el)}
        className="scroll-section initial-visible"
      >
        <section className="glv-intro">
          <div className="glv-container">
            <div className="glv-intro-content">
              <div className="glv-intro-description">
                <h2>Globe Valves</h2>
                <p>
                  The Globe valve is used for throttling purposes. The most used standards of
                  reference are API 623 and BS 1873. Materials available include carbon steel, low
                  temperature carbon steel, low alloy steel, austenitic stainless steel, austenitic-ferritic
                  duplex stainless steel, and nickel alloys.
                </p>
              </div>
              <div className="glv-intro-image">
                <img src={p3} alt="Globe Valves" className="glv-intro-img" />
              </div>
            </div>
            <div className="glv-specs-row">
              <div className="glv-spec-card-horizontal">
                <h3>Product Range</h3>
                <ul>
                  <li>Piping component size : 2" ~ 16" (DN50-DN400)</li>
                  <li>Pressure : ANSI Class 150~2500</li>
                  <li>Temperature : -196℃~ 500℃</li>
                  <li>Body Materials : Carbon Steel, Stainless Steel, Alloy Steel, Duplex Steel</li>
                  <li>Trim (Disc/Stem) : Carbon Steel, Stainless Steel, Duplex Steel, Alloy Steel</li>
                </ul>
              </div>
              <div className="glv-spec-card-horizontal">
                <h3>Technical Specifications</h3>
                <ul>
                  <li>Design Standard : BS1873, API 623</li>
                  <li>Structure length : ANSI B16.10</li>
                  <li>Pipe flanges : ANSI B16.5</li>
                  <li>Butt end size : ANSI B16.25</li>
                  <li>Inspection and Testing : ISO 12266 1 & 2, API598</li>
                  <li>Transmission : manual, electric, bevel gear</li>
                  <li>Special : NACE MR-01-75, IBR</li>
                </ul>
              </div>
              <div className="glv-spec-card-horizontal">
                <h3>Design Features</h3>
                <ul>
                  <li>Outside Screw and Yoke (OS & Y), RS or NRS</li>
                  <li>Bolted Bonnet</li>
                  <li>Conic Disc, Parabolic</li>
                  <li>Extended Bonnet for Cryogenic Services</li>
                  <li>Impact hand wheel as an optional</li>
                  <li>Gear Optional</li>
                  <li>Locking Device as an Optional</li>
                </ul>
              </div>
            </div>

            {/* Product Availability Matrix */}
            <div className="glv-specification-table-section">
              <h2 className="glv-table-title">Product Availability Matrix</h2>
              <img src={globeMatrix} alt="Product Availability Matrix" />
              <p className="glv-legend">Legends : BB - Bolted Bonnet, BC - Bolted Cover, PS - Pressure Seal</p>
            </div>

            {/* GBVMOC Material Options Section */}
            <div className="glv-material-options-section">
              <h2 className="glv-table-title">GBVMOC</h2>
              <div className="glv-material-content">
                <div className="glv-material-image">
                  <img src={globe1} alt="Globe Valve Parts" />
                </div>
                <div className="glv-material-table-container">
                  <table className="glv-material-table">
                    <thead>
                      <tr>
                        <th>PART NO</th>
                        <th>PART</th>
                        <th>MATERIAL OPTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>1</td>
                        <td>BODY</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>2</td>
                        <td>BONNET</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>3</td>
                        <td>SEAT RING</td>
                        <td>ASTM A105 + STELLITE 6 / A 351 CF8M / A 351 CF3M / A 217 CA 15 / DUPLEX SS / INCONEL / MONEL (HARD FACING AS PER REQUIREMENT).</td>
                      </tr>
                      <tr>
                        <td>4</td>
                        <td>PLUG</td>
                        <td>ASTM - A 216 WCB + CRA OVERLAY / A 351 CF8M / A 351 CF3M / HARD FACING OVERLAY AS PER REQUIREMENT.</td>
                      </tr>
                      <tr>
                        <td>5</td>
                        <td>STEM</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>6</td>
                        <td>PLUG NUT</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>7</td>
                        <td>BODY GASKET</td>
                        <td>GRAPHITE / REINFORCED SPIRAL-WOUND SS 316 GRAPHITE.</td>
                      </tr>
                      <tr>
                        <td>8</td>
                        <td>BACK SEAT BUSH</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>9</td>
                        <td>GLAND PACKING</td>
                        <td>DIE MOULDED GRAPHOIL / FLEXIBLE GRAPHITE WITH SACRIFICIAL CORROSION INHIBITOR AND INCONEL WIRE REINFORCEMENT / TOP & BOTTOM BRAIDED GRAPHITE.</td>
                      </tr>
                      <tr>
                        <td>10</td>
                        <td>GLAND BUSH</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>11</td>
                        <td>GLAND FLANGE</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>12</td>
                        <td>BODY STUDS / NUTS</td>
                        <td>ASTM - A 320 L7, L7M / A 193 B8M / A 193 B7 / A 193 GR. B7M, ASTM A194 GR. 7/7M, 2H, 2HM, 8, 8M.</td>
                      </tr>
                      <tr>
                        <td>13</td>
                        <td>GLAND STUD / NUTS</td>
                        <td>ASTM - A 320 L7, L7M / A 193 B8M / A 193 B7 / A 193 GR. B7M, ASTM A194 GR. 7/7M, 2H, 2HM, 8, 8M.</td>
                      </tr>
                      <tr>
                        <td>14</td>
                        <td>HANDWHEEL</td>
                        <td>S G IRON, MALLEABLE IRON, FAB STEEL.</td>
                      </tr>
                      <tr>
                        <td>15</td>
                        <td>YOKE NUT</td>
                        <td>ASTM A439 Gr.D2, PHOSPOROUS BRONZE.</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="glv-material-note">Note: Materials not listed above can be offered on request.</p>
                </div>
              </div>
            </div>

            {/* Dimensional Details Tables */}
            <div className="glv-dimensional-details-section">
              {/* 150 Class Table */}
              <div className="glv-dimensional-table-wrapper">
                <h3 className="glv-dimensional-title">GLOBE VALVE DIMENSIONAL DETAILS FOR 150 CLASS</h3>
                <div className="glv-table-scroll-container">
                  <table className="glv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>ØHW</th>
                        <th>Weight Kg</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050)</td><td>51</td><td>203</td><td>203</td><td>216</td><td>152</td><td>152.4</td><td>19.1</td><td>4</td><td>91.9</td><td>14.2</td><td>1.5</td><td>200</td><td>21</td></tr>
                      <tr><td>3" (080)</td><td>77</td><td>267</td><td>267</td><td>283</td><td>190</td><td>152.4</td><td>19.1</td><td>4</td><td>127</td><td>17.5</td><td>1.5</td><td>250</td><td>35</td></tr>
                      <tr><td>4" (100)</td><td>102</td><td>305</td><td>305</td><td>318</td><td>229</td><td>190.5</td><td>19.1</td><td>8</td><td>157.2</td><td>22.4</td><td>1.5</td><td>300</td><td>56</td></tr>
                      <tr><td>6" (150)</td><td>153</td><td>403</td><td>403</td><td>419</td><td>279</td><td>241.3</td><td>22.2</td><td>8</td><td>215.9</td><td>23.9</td><td>1.5</td><td>350</td><td>95</td></tr>
                      <tr><td>8" (200)</td><td>204</td><td>419</td><td>419</td><td>432</td><td>343</td><td>298.4</td><td>22.2</td><td>8</td><td>269.7</td><td>26.9</td><td>1.5</td><td>400</td><td>140</td></tr>
                      <tr><td>10" (250)</td><td>254</td><td>457</td><td>457</td><td>470</td><td>406</td><td>362</td><td>25.4</td><td>12</td><td>323.9</td><td>28.4</td><td>1.5</td><td>450</td><td>220</td></tr>
                      <tr><td>12" (300)</td><td>305</td><td>502</td><td>502</td><td>515</td><td>483</td><td>431.8</td><td>25.4</td><td>12</td><td>381</td><td>30.2</td><td>1.5</td><td>500</td><td>320</td></tr>
                      <tr><td>14" (350)</td><td>337</td><td>572</td><td>572</td><td>585</td><td>533</td><td>476.2</td><td>28.6</td><td>12</td><td>412.8</td><td>33.3</td><td>1.5</td><td>600</td><td>450</td></tr>
                      <tr><td>16" (400)</td><td>388</td><td>610</td><td>610</td><td>623</td><td>597</td><td>539.8</td><td>28.6</td><td>16</td><td>469.9</td><td>35.1</td><td>1.5</td><td>750</td><td>725</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 300 Class Table */}
              <div className="glv-dimensional-table-wrapper">
                <h3 className="glv-dimensional-title">GLOBE VALVE DIMENSIONAL DETAILS FOR 300 CLASS</h3>
                <div className="glv-table-scroll-container">
                  <table className="glv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>ØHW</th>
                        <th>Weight Kg</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050)</td><td>51</td><td>267</td><td>267</td><td>283</td><td>165</td><td>127</td><td>19.1</td><td>8</td><td>91.9</td><td>20.6</td><td>1.5</td><td>250</td><td>26</td></tr>
                      <tr><td>3" (080)</td><td>77</td><td>318</td><td>318</td><td>334</td><td>210</td><td>168.1</td><td>22.2</td><td>8</td><td>127</td><td>26.9</td><td>1.5</td><td>300</td><td>52</td></tr>
                      <tr><td>4" (100)</td><td>102</td><td>356</td><td>356</td><td>372</td><td>254</td><td>200.2</td><td>22.2</td><td>8</td><td>157.2</td><td>30.2</td><td>1.5</td><td>350</td><td>75</td></tr>
                      <tr><td>6" (150)</td><td>153</td><td>444</td><td>444</td><td>460</td><td>318</td><td>269.7</td><td>22.2</td><td>12</td><td>215.9</td><td>35.1</td><td>1.5</td><td>450</td><td>173</td></tr>
                      <tr><td>8" (200)</td><td>204</td><td>559</td><td>559</td><td>575</td><td>381</td><td>330.2</td><td>25.4</td><td>12</td><td>269.7</td><td>39.6</td><td>1.5</td><td>550</td><td>297</td></tr>
                      <tr><td>10" (250)</td><td>254</td><td>622</td><td>622</td><td>638</td><td>444</td><td>387.4</td><td>28.6</td><td>16</td><td>323.9</td><td>46</td><td>1.5</td><td>600</td><td>500</td></tr>
                      <tr><td>12" (300)</td><td>305</td><td>711</td><td>711</td><td>727</td><td>521</td><td>450.8</td><td>31.8</td><td>16</td><td>381</td><td>30.2</td><td>1.5</td><td>500</td><td>724</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 600 Class Table */}
              <div className="glv-dimensional-table-wrapper">
                <h3 className="glv-dimensional-title">GLOBE VALVE DIMENSIONAL DETAILS FOR 600 CLASS</h3>
                <div className="glv-table-scroll-container">
                  <table className="glv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>ØHW</th>
                        <th>Weight Kg</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050)</td><td>51</td><td>292</td><td>292</td><td>295</td><td>165</td><td>127</td><td>19.1</td><td>8</td><td>91.9</td><td>25.4</td><td>6.4</td><td>300</td><td>37</td></tr>
                      <tr><td>3" (080)</td><td>77</td><td>356</td><td>356</td><td>359</td><td>210</td><td>168.1</td><td>22.2</td><td>8</td><td>127</td><td>31.8</td><td>6.4</td><td>350</td><td>62</td></tr>
                      <tr><td>4" (100)</td><td>102</td><td>432</td><td>432</td><td>435</td><td>273</td><td>215.9</td><td>25.4</td><td>8</td><td>157.2</td><td>38.1</td><td>6.4</td><td>450</td><td>150</td></tr>
                      <tr><td>6" (150)</td><td>153</td><td>559</td><td>559</td><td>562</td><td>356</td><td>292.1</td><td>28.6</td><td>12</td><td>215.9</td><td>47.8</td><td>6.4</td><td>550</td><td>294</td></tr>
                      <tr><td>8" (200)</td><td>200</td><td>660</td><td>660</td><td>663</td><td>419</td><td>349.2</td><td>31.8</td><td>12</td><td>269.7</td><td>55.6</td><td>6.4</td><td>600</td><td>543</td></tr>
                      <tr><td>10" (250)</td><td>254</td><td>787</td><td>787</td><td>790</td><td>508</td><td>431.8</td><td>35.1</td><td>16</td><td>323.9</td><td>63.5</td><td>6.4</td><td>600</td><td>1006</td></tr>
                      <tr><td>12" (300)</td><td>305</td><td>838</td><td>838</td><td>841</td><td>559</td><td>489</td><td>35.1</td><td>20</td><td>381</td><td>66.5</td><td>6.4</td><td>600</td><td>1350</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* PSGV MOC Section */}
            <div className="glv-psgv-moc-section">
              <h2 className="glv-table-title">PSGBV MOC</h2>
              <div className="glv-psgv-content">
                <div className="glv-psgv-image">
                  <img src={globe2} alt="Pressure Seal Globe Valve Parts" />
                </div>
                <div className="glv-psgv-table-container">
                  <table className="glv-material-table">
                    <thead>
                      <tr>
                        <th>PART NO</th>
                        <th>PART</th>
                        <th>MATERIAL OPTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>1</td>
                        <td>BODY</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>2</td>
                        <td>BONNET</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>3</td>
                        <td>SEAT RING</td>
                        <td>ASTM A105 + STELLITE 6 / A 351 CF8M / A 351 CF3M / A 217 CA 15 / DUPLEX SS / INCONEL / MONEL (HARD FACING AS PER REQUIREMENT).</td>
                      </tr>
                      <tr>
                        <td>4</td>
                        <td>PLUG</td>
                        <td>ASTM - A 216 WCB + CRA OVERLAY / A351 CF8M / A 351 CF3M / HARD FACING OVERLAY AS PER REQUIREMENT.</td>
                      </tr>
                      <tr>
                        <td>5</td>
                        <td>YOKE</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>6</td>
                        <td>THRUST PAD</td>
                        <td>A 479 SS 316 / A 479 SS 316L.</td>
                      </tr>
                      <tr>
                        <td>7</td>
                        <td>SPLIT WASHER</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>8</td>
                        <td>PLUG NUT</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>9</td>
                        <td>STEM</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>10</td>
                        <td>PRESSURE SEAL GASKET</td>
                        <td>GRAPHITE / DIE MOULDED GRAPHITE.</td>
                      </tr>
                      <tr>
                        <td>11</td>
                        <td>SPACER RING</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>12</td>
                        <td>SEGMENTAL RING</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>13</td>
                        <td>FACE RING</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>14</td>
                        <td>LOCKING RING</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>15</td>
                        <td>BACK SEAT BUSH</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>16</td>
                        <td>GLAND PACKING</td>
                        <td>DIE MOULDED GRAPHOIL / FLEXIBLE GRAPHITE WITH SACRIFICIAL CORROSION INHIBITOR AND INCONEL WIRE REINFORCEMENT / TOP & BOTTOM BRAIDED GRAPHITE.</td>
                      </tr>
                      <tr>
                        <td>17</td>
                        <td>PROTECTIVE RING</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>18</td>
                        <td>GLAND BUSH</td>
                        <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                      </tr>
                      <tr>
                        <td>19</td>
                        <td>GLAND FLANGE</td>
                        <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                      </tr>
                      <tr>
                        <td>20</td>
                        <td>STEM GUIDE</td>
                        <td>ASTM A106 Gr.B / SS 304 / SS 316.</td>
                      </tr>
                      <tr>
                        <td>21</td>
                        <td>GUIDE KEY</td>
                        <td>EN8.</td>
                      </tr>
                      <tr>
                        <td>22</td>
                        <td>GEAR BOX</td>
                        <td>COMMERCIAL.</td>
                      </tr>
                      <tr>
                        <td>23</td>
                        <td>YOKE STUDS / NUTS</td>
                        <td>ASTM - A 320 L7, L7M / A 193 B8M / A 193 B7 / A 193 GR. B7M, ASTM A194 GR. 7/7M, 2H, 2HM, 8, 8M.</td>
                      </tr>
                      <tr>
                        <td>24</td>
                        <td>GLAND STUD / NUTS</td>
                        <td>ASTM - A 320 L7, L7M / A 193 B8M / A 193 B7 / A 193 GR. B7M, ASTM A194 GR. 7/7M, 2H, 2HM, 8, 8M.</td>
                      </tr>
                      <tr>
                        <td>25</td>
                        <td>GEAR BOLTS</td>
                        <td>HIGH TENSILE.</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="glv-material-note">Note: Materials not listed above can be offered on request.</p>
                </div>
              </div>
            </div>

            {/* Pressure Seal Globe Valve Dimensional Details Tables */}
            <div className="glv-dimensional-details-section">
              {/* 900 Class Table */}
              <div className="glv-dimensional-table-wrapper">
                <h3 className="glv-dimensional-title">PRESSURE SEAL GLOBE VALVE DIMENSIONAL DETAILS FOR 900 CLASS</h3>
                <div className="glv-table-scroll-container">
                  <table className="glv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>ØHW</th>
                        <th>Weight Kg</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050)</td><td>48</td><td>368</td><td>368</td><td>371</td><td>216</td><td>165.1</td><td>25.4</td><td>8</td><td>91.9</td><td>38.1</td><td>6.4</td><td>350</td><td>84</td></tr>
                      <tr><td>3" (080)</td><td>73</td><td>381</td><td>381</td><td>384</td><td>241</td><td>190.5</td><td>25.4</td><td>8</td><td>127</td><td>38.1</td><td>6.4</td><td>350</td><td>116</td></tr>
                      <tr><td>4" (100)</td><td>99</td><td>457</td><td>457</td><td>460</td><td>292</td><td>235</td><td>31.8</td><td>8</td><td>157.2</td><td>44.5</td><td>6.4</td><td>450</td><td>179</td></tr>
                      <tr><td>6" (150)</td><td>147</td><td>610</td><td>610</td><td>613</td><td>381</td><td>317.5</td><td>31.8</td><td>12</td><td>215.9</td><td>55.6</td><td>6.4</td><td>600</td><td>441</td></tr>
                      <tr><td>8" (200)</td><td>191</td><td>737</td><td>737</td><td>740</td><td>470</td><td>393.7</td><td>38.1</td><td>12</td><td>269.7</td><td>63.5</td><td>6.4</td><td>600</td><td>1050</td></tr>
                      <tr><td>10" (250)</td><td>238</td><td>838</td><td>838</td><td>841</td><td>546</td><td>469.9</td><td>38.1</td><td>16</td><td>323.9</td><td>69.8</td><td>6.4</td><td>750</td><td>1720</td></tr>
                      <tr><td>12" (300)</td><td>283</td><td>965</td><td>965</td><td>968</td><td>610</td><td>533.4</td><td>38.1</td><td>20</td><td>381</td><td>79.2</td><td>6.4</td><td>750</td><td>2300</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 1500 Class Table */}
              <div className="glv-dimensional-table-wrapper">
                <h3 className="glv-dimensional-title">PRESSURE SEAL GLOBE VALVE DIMENSIONAL DETAILS FOR 1500 CLASS</h3>
                <div className="glv-table-scroll-container">
                  <table className="glv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>ØHW</th>
                        <th>Weight Kg</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050)</td><td>48</td><td>368</td><td>368</td><td>371</td><td>216</td><td>165.1</td><td>25.4</td><td>8</td><td>91.9</td><td>38.1</td><td>6.4</td><td>350</td><td>85</td></tr>
                      <tr><td>3" (080)</td><td>70</td><td>470</td><td>470</td><td>473</td><td>267</td><td>203.2</td><td>31.8</td><td>8</td><td>127</td><td>47.8</td><td>6.4</td><td>750</td><td>143</td></tr>
                      <tr><td>4" (100)</td><td>92</td><td>546</td><td>546</td><td>549</td><td>311</td><td>241.3</td><td>35.1</td><td>8</td><td>157.2</td><td>53.8</td><td>6.4</td><td>550</td><td>236</td></tr>
                      <tr><td>6" (150)</td><td>137</td><td>705</td><td>705</td><td>711</td><td>394</td><td>317.5</td><td>38.1</td><td>12</td><td>215.9</td><td>82.6</td><td>6.4</td><td>600</td><td>918</td></tr>
                      <tr><td>8" (200)</td><td>178</td><td>832</td><td>832</td><td>842</td><td>483</td><td>393.7</td><td>41.1</td><td>12</td><td>269.7</td><td>91.9</td><td>6.4</td><td>750</td><td>1764</td></tr>
                      <tr><td>10" (250)</td><td>223</td><td>991</td><td>991</td><td>1001</td><td>584</td><td>482.6</td><td>50.8</td><td>12</td><td>323.9</td><td>108</td><td>6.4</td><td>750</td><td>2680</td></tr>
                      <tr><td>12" (300)</td><td>264</td><td>1130</td><td>1130</td><td>1146</td><td>673</td><td>571.5</td><td>53.9</td><td>16</td><td>381</td><td>124</td><td>6.4</td><td>750</td><td>3400</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 2500 Class Table */}
              <div className="glv-dimensional-table-wrapper">
                <h3 className="glv-dimensional-title">PRESSURE SEAL GLOBE VALVE DIMENSIONAL DETAILS FOR 2500 CLASS</h3>
                <div className="glv-table-scroll-container">
                  <table className="glv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>ØHW</th>
                        <th>Weight Kg</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050)</td><td>39</td><td>451</td><td>451</td><td>454</td><td>235</td><td>171.4</td><td>28.6</td><td>8</td><td>91.9</td><td>50.8</td><td>6.4</td><td>450</td><td>105</td></tr>
                      <tr><td>3" (080)</td><td>58</td><td>578</td><td>578</td><td>584</td><td>305</td><td>228.6</td><td>35.1</td><td>8</td><td>127</td><td>66.5</td><td>6.4</td><td>600</td><td>221</td></tr>
                      <tr><td>4" (100)</td><td>73</td><td>673</td><td>673</td><td>683</td><td>356</td><td>273</td><td>41.3</td><td>8</td><td>157.2</td><td>76.2</td><td>6.4</td><td>600</td><td>525</td></tr>
                      <tr><td>6" (150)</td><td>111</td><td>914</td><td>914</td><td>927</td><td>483</td><td>368.3</td><td>53.9</td><td>8</td><td>215.9</td><td>108.0</td><td>6.4</td><td>750</td><td>1313</td></tr>
                      <tr><td>8" (200)</td><td>147</td><td>1022</td><td>1022</td><td>1038</td><td>552</td><td>438.2</td><td>53.9</td><td>12</td><td>269.7</td><td>127.0</td><td>6.4</td><td>750</td><td>2520</td></tr>
                      <tr><td>10" (250)</td><td>185</td><td>1270</td><td>1270</td><td>1292</td><td>673</td><td>539.8</td><td>66.7</td><td>12</td><td>323.9</td><td>165.1</td><td>6.4</td><td>750</td><td>3300</td></tr>
                      <tr><td>12" (300)</td><td>219</td><td>1422</td><td>1422</td><td>1444</td><td>762</td><td>619.3</td><td>73</td><td>12</td><td>381</td><td>184.2</td><td>6.4</td><td>750</td><td>4200</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
      {scrollY > 300 && (
        <button 
          className="scroll-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            background: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            cursor: 'pointer',
            fontSize: '1.2rem',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
            transition: 'all 0.3s ease'
          }}
        >
          ↑
        </button>
      )}
    </>
  );
};

export default GlobeValves;

